import React, { useMemo } from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';

import useMediaQuery from '@mui/material/useMediaQuery';

import { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import Loader from 'components/uiLibrary/Loader';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import InfiniteListPage, { PageLoading } from 'components/Globals/Layout/InfiniteListPage';
import GallerySpaceFiller from 'components/Globals/Media/GallerySpaceFiller';
// NOTE: adding es lint ignore dependency cycle issue for commit
// eslint-disable-next-line import/no-cycle
import SectionBlock from 'components/Globals/SectionBlock';

import {
  TP,
  UPCOMING_PRODUCTIONS_MEDIA_SORT_ORDER,
  SORT_OPTION_VALUES,
  MEDIA_SHOWREEL_OPTIONS,
} from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';
import { getFillerText } from 'utils/media';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Media/queries';

import { useTranslation } from 'src/i18n';

import { FILTER_TYPES, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import DigitalVideoListing from '../DigitalVideoListing';
import VideoCard from '../VideoCard';

import classes from './VideoListing.module.scss';

export const VIDEO_SECTION_TYPES = {
  DIGITAL: 'digital',
};

const VideoGroup = ({
  videos,
  fullWidthCard = false,
  withProductionInfo = false,
  trackingData,
  cardCountPerRow = 0,
  fillerText,
}) => {
  const videosArray = videos || [];
  const lastItem = videosArray[videosArray.length - 1];
  const urls = lastItem?.thumbnail?.urls;
  const showFiller = !!(!fullWidthCard && cardCountPerRow && videosArray.length % cardCountPerRow > 0);

  return (
    <div
      className={classnames(classes.group, {
        [classes.column_full]: fullWidthCard,
      })}
    >
      {videos?.map(video => (
        <VideoCard
          key={video?.id}
          data={video}
          withProductionInfo={video?.production?.id && withProductionInfo}
          fullWidth={fullWidthCard}
          trackingData={{ ...trackingData, entityId: video?.id }}
        />
      ))}
      {showFiller && (
        <div
          className={classnames(classes.filler, {
            [classes.filler__full]: videosArray.length % cardCountPerRow === 1,
          })}
        >
          <GallerySpaceFiller
            text={fillerText}
            image={urls?.large || urls?.medium || urls?.small || urls?.original}
            transformations={TRANSFORMATIONS.GALLERY_PORTRAIT_VIEW}
          />
        </div>
      )}
    </div>
  );
};

const AllVideos = ({ limit, entity, trackingData }) => {
  const { entityType, entityId, mainPath } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { filterParams } = obRouteContext || {};
  const isFullWidthCard = useMediaQuery('(max-width: 800px)');
  const isFeaturedVideosExist = entity?.stats?.media?.videos?.featured?.exists;

  return (
    <InfiniteListPage
      query={queries.getFeaturedVideos({
        entityType,
        entityId,
        filters: filterParams,
        limit,
        queryConfig: {
          enabled: isFeaturedVideosExist,
        },
      })}
      disableQueryUpdate
      disabled={!isFeaturedVideosExist}
    >
      {({ pages, hasNextPage }) => (
        <>
          <VideoGroup
            videos={pages?.flatMap(item => item.data)}
            fullWidthCard={!mainPath || isFullWidthCard}
            trackingData={trackingData}
            fillerText={getFillerText({ entity, entityType })}
            cardCountPerRow={3}
          />
          {hasNextPage && <PageLoading defaultLoader />}
          {(!hasNextPage || !isFeaturedVideosExist) && <ShowreelVideos limit={limit} trackingData={trackingData} />}
        </>
      )}
    </InfiniteListPage>
  );
};

const ShowreelVideos = ({ trackingData, limit }) => {
  const { entityType, entityId, mainPath, entity } = usePageContext();
  const isFullWidthCard = useMediaQuery('(max-width: 800px)');
  const isShowreelExists = entity?.stats?.showreels?.intro?.exists || entity?.stats?.showreels?.audition?.exists;
  return (
    <InfiniteListPage
      query={queries.getShowreelVideos({
        ids: [MEDIA_SHOWREEL_OPTIONS.AUDITION.id, MEDIA_SHOWREEL_OPTIONS.INTRO.id],
        entityType,
        entityId,
        limit,
        queryConfig: { enabled: isShowreelExists },
      })}
      disableQueryUpdate
      disabled={!isShowreelExists}
    >
      {({ pages, hasNextPage }) => (
        <>
          <VideoGroup
            videos={pages?.flatMap(item => item.data)}
            fullWidthCard={!mainPath || isFullWidthCard}
            trackingData={trackingData}
            fillerText={getFillerText({ entity, entityType })}
            cardCountPerRow={3}
          />
          {(!hasNextPage || !isShowreelExists) && <AllProductionVideos limit={limit} trackingData={trackingData} />}
          {hasNextPage && <PageLoading defaultLoader />}
        </>
      )}
    </InfiniteListPage>
  );
};

const AllProductionVideos = ({ trackingData, limit }) => {
  const { entityType, entityId, filterParams, mainPath, entity } = usePageContext();
  const isFullWidthCard = useMediaQuery('(max-width: 800px)');

  return (
    <div>
      <InfiniteListPage
        query={queries.getProductionVideos({
          entityType,
          entityId,
          limit,
          filters: {
            ...filterParams,
            sort: UPCOMING_PRODUCTIONS_MEDIA_SORT_ORDER,
          },
          queryConfig: {
            enabled: entity?.stats?.media?.videos?.production?.exists,
          },
        })}
        disableQueryUpdate
        disabled={!entity?.stats?.media?.videos?.production?.exists}
      >
        {({ pages, hasNextPage }) => (
          <>
            <VideoGroup
              videos={pages?.flatMap(item => item.data)}
              fullWidthCard={!mainPath || isFullWidthCard}
              withProductionInfo
              trackingData={trackingData}
              fillerText={getFillerText({ entity, entityType })}
              cardCountPerRow={3}
            />
            {hasNextPage && <PageLoading defaultLoader />}
          </>
        )}
      </InfiniteListPage>
    </div>
  );
};

const DigitalVideos = ({ entity, title, limit, sectionProps = {}, trackingData }) => {
  const { entityType, navigate } = usePageContext();

  const linkProps = useMemo(
    () =>
      navigate.getLinkProps({
        entityType,
        entity,
        tab: ROUTE_RESERVED_KEYWORDS.performances,
        pro: false,
        filters: {
          [FILTER_TYPES.WATCH_ONLINE]: true,
        },
      }),
    [entity, entityType, navigate],
  );

  return (
    <SectionBlock
      title={title}
      linkProps={linkProps}
      enableSeeAll
      {...sectionProps}
      seeAllTrackingData={{ ...trackingData, component: COMPONENTS.SEE_ALL_CTA }}
    >
      <DigitalVideoListing limit={limit} trackingData={trackingData} mediaSlugInCarousel />
    </SectionBlock>
  );
};

const FeaturedVideos = ({ limit = 10, sectionProps = {}, trackingData }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { obRouteContext } = useAppContext();
  const { page, filterParams } = obRouteContext || {};
  const { entityType } = page;
  const { navigate, entity } = usePageContext();
  const isFullWidthCard = useMediaQuery('(max-width: 800px)');
  const { data, isLoading } = useQuery(
    queries.getFeaturedVideos({ entityId: entity?.id, entityType, filters: filterParams, limit }),
  );

  const { data: productionVideos } = useQuery(
    queries.getProductionVideos({
      entityType,
      entityId: entity?.id,
      filters: {
        ...filterParams,
        sort: SORT_OPTION_VALUES.CUSTOM_DATE,
      },
      limit: limit - (entity?.stats?.media?.videos?.featured?.total || 0),
      queryConfig: {
        enabled:
          (entity?.stats?.media?.videos?.featured?.total || 0) < limit &&
          entity?.stats?.media?.videos?.production?.exists,
      },
    }),
  );

  const { linkProps, videos } = useMemo(() => {
    if (data?.data?.length === 0 && productionVideos?.data?.length === 0) {
      return { linkProps: {}, photos: [] };
    }

    const items = [...(data?.data || [])];

    if (items?.length <= limit && productionVideos?.data?.length) {
      items.push(...(productionVideos?.data || []));
    }

    return {
      linkProps: navigate.getLinkProps({ entityType, entity, path: ROUTE_RESERVED_KEYWORDS.videos, pro: false }),
      videos: items,
    };
  }, [data?.data, entity, entityType, limit, navigate, productionVideos?.data]);

  return (
    <SectionBlock
      title={t('FEATURED_VIDEOS')}
      linkProps={linkProps}
      seeAllTrackingData={{ ...trackingData, component: COMPONENTS.SEE_ALL_CTA }}
      {...sectionProps}
    >
      <VideoGroup videos={videos} fullWidthCard={isFullWidthCard} trackingData={trackingData} withProductionInfo />
      {isLoading && <Loader />}
    </SectionBlock>
  );
};

const VideoListing = ({ title, entity, type, limit = 10, sectionProps, trackingData, isOverview }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');

  if (isOverview) {
    return <FeaturedVideos entity={entity} limit={limit} sectionProps={sectionProps} trackingData={trackingData} />;
  }

  if (type === VIDEO_SECTION_TYPES.DIGITAL) {
    return (
      <DigitalVideos
        title={title || t(`${TP}.FN_WATCH_ONLINE_LS_VOD`)}
        entity={entity}
        limit={limit}
        sectionProps={sectionProps}
        trackingData={trackingData}
      />
    );
  }

  return (
    <SectionBlock
      title={t(`${TP}.FN_TAB_VIDEOS`)}
      styles={{
        root: classes.allMedia,
      }}
      {...sectionProps}
    >
      <AllVideos entity={entity} limit={limit} trackingData={trackingData} />
    </SectionBlock>
  );
};

VideoListing.propTypes = {
  type: PropTypes.oneOf([VIDEO_SECTION_TYPES.DIGITAL]),
  limit: PropTypes.number,
};

export default VideoListing;
